import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { ThemeProvider } from "@/lib/theme";

import Nav from "@/components/Nav";
import Spine from "@/components/Spine";
import Hero from "@/components/Hero";
import CodeMarquee from "@/components/CodeMarquee";
import PainSection from "@/components/PainSection";
import MechanismSection from "@/components/MechanismSection";
import CanonicalLayer from "@/components/CanonicalLayer";
import ProductProof from "@/components/ProductProof";
import FrameworksGrid from "@/components/FrameworksGrid";
import BreadthEdge from "@/components/BreadthEdge";
import ModulesSlab from "@/components/ModulesSlab";
import HashChain from "@/components/HashChain";
import Pricing from "@/components/Pricing";
import FAQSection from "@/components/FAQSection";
import Founders from "@/components/Founders";
import DemoForm from "@/components/DemoForm";
import Footer from "@/components/Footer";
import PrivacyPage from "@/pages/PrivacyPage";

function ScrollToHash() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo({ top: 0, behavior: "auto" }); return; }
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(t);
  }, [hash, pathname]);
  return null;
}

function LenisProvider({ children }) {
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });

    let raf = 0;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return children;
}

function LandingPage() {
  return (
    <div className="App grain">
      <Spine />
      <Nav />
      <main>
        <Hero />
        <CodeMarquee />
        <PainSection />
        <MechanismSection />
        <CanonicalLayer />
        <ProductProof />
        <FrameworksGrid />
        <BreadthEdge />
        <ModulesSlab />
        <HashChain />
        <Pricing />
        <FAQSection />
        <Founders />
        <DemoForm />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <LenisProvider>
          <ScrollToHash />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="*" element={<LandingPage />} />
          </Routes>
        </LenisProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
